import React, { useState, useEffect, useRef } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  fireConfetti,
  displayMessage,
  scrollToMessage,
} from '../utils/functions';
import Spinner from '../utils/spinner';
import Config from '../utils/Config';
import "./Login.css";
import './CreateAccount.css';

const CreateAccount = ({ email: propEmail }) => {
  const { token: urlToken } = useParams();
  const navigate = useNavigate();
  const inputRef = useRef(null);
  const messageRef = useRef(null);

  const [messageClass, setMessageClass] = useState("warning hidden");
  const [formData, setFormData] = useState({
    firstName: "",
    lastName: "",
    timeZone: "",
    password: "",
    confirmPassword: "",
    useMFA: false,
    acceptTOU: false,
  });
  const [timeZones, setTimeZones] = useState([]);
  const [showPassword, setShowPassword] = useState(false);
  const [accountCreated, setAccountCreated] = useState(false);
  const [token, setToken] = useState('');
  const [email, setEmail] = useState(propEmail || "");
  const [acceptedTos, setAcceptedTos] = useState(false);
  const [createAccount, setCreateAccount] = useState(false);
  const [passwordScore, setPasswordScore] = useState(0);
  const passwordsMatch =
    formData.password &&
    formData.confirmPassword &&
    formData.password === formData.confirmPassword;

  // 🔍 Autofocus
  useEffect(() => {
    inputRef.current?.focus();
  }, []);

  // ✅ Load token from URL or localStorage
  useEffect(() => {
    const regToken = localStorage.getItem('regToken');
    if (regToken) {
      setToken(regToken);
      localStorage.removeItem("regToken");
    } else if (urlToken) {
      setToken(urlToken);
      fetchDetailsFromToken(urlToken);
    }
  }, [urlToken]);

  // 🔁 Load timezones
  useEffect(() => {
    const fetchTimeZones = async () => {
      try {
        const res = await fetch(`${Config.API.API_BASE_URL}${Config.ENDPOINTS.USER_TIMEZONES}`);
        const data = await res.json();
        setTimeZones(data);

        // ⏱ Auto-detect browser time zone
        const detectedZone = Intl.DateTimeFormat().resolvedOptions().timeZone;

        // If it matches your data, auto-set it
        const match = data.find(tz => tz.value === detectedZone);
        if (match) {
          setFormData(prev => ({ ...prev, timeZone: detectedZone }));
        }

      } catch (err) {
        console.error("❌ Error fetching timezones:", err);
      }
    };
    fetchTimeZones();
  }, []);

  useEffect(() => {
    if (accountCreated) {
      const timer = setTimeout(() => {
        setAccountCreated(false);
        navigate("/login"); // 👈 redirect after 3s
      }, 3000);
      return () => clearTimeout(timer);
    }
  }, [accountCreated, navigate]);

  // 📨 Pull email from token-based endpoint
  const fetchDetailsFromToken = async (verificationToken) => {
    try {
      const res = await fetch(`${Config.API.API_BASE_URL}${Config.ENDPOINTS.USER_FETCH_VERIFICATION_DETAILS}`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ token: verificationToken }),
      });

      const data = await res.json();
      if (res.ok && data.email) {
        setEmail(data.email);
      } else {
        messageRef.current.innerHTML = data.message || "❌ Invalid or expired token.";
        setMessageClass("warning");
        messageRef.current.style.display = "block";
      }
    } catch (err) {
      messageRef.current.innerHTML = "❌ Server error retrieving token details.";
      setMessageClass("warning");
      messageRef.current.style.display = "block";
    }
  };

  const validateForm = () => {
    if (!formData.firstName.trim()) {
      displayMessage(messageRef, `❌ First name is required.`, 'warning', setMessageClass);
      return false;
    }
    if (!formData.lastName.trim()) {
      displayMessage(messageRef, `❌ Last name is required.`, 'warning', setMessageClass);
      return false;
    }
    if (!formData.timeZone.trim()) {
      displayMessage(messageRef, `❌ Time zone is required.`, 'warning', setMessageClass);
      return false;
    }
    if (!formData.password.trim()) {
      displayMessage(messageRef, `❌ Password is required.`, 'warning', setMessageClass);
      return false;
    }
    if (!formData.confirmPassword.trim()) {
      displayMessage(messageRef, `❌ Confirm password is required.`, 'warning', setMessageClass);
      return false;
    }
    if (formData.password.length < 8) {
      displayMessage(messageRef, `❌ Password must be at least 8 characters long.`, 'warning', setMessageClass);
      return false;
    }
    if (formData.password !== formData.confirmPassword) {
      displayMessage(messageRef, `❌ Passwords do not match!`, 'warning', setMessageClass);
      return false;
    }
    if (!formData.acceptTOU) {
      displayMessage(messageRef, `❌ You must accept the Terms of Use to continue.`, 'warning', setMessageClass);
      return false;
    }
    return true;
  };

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData(prev => ({ ...prev, [name]: type === "checkbox" ? checked : value }));

    if (name === "password") {
      const strengthScore = scorePassword(value);
      setPasswordScore(strengthScore);
    }
  };

  const handleRegister = async (e) => {
    e.preventDefault();
    if (!validateForm()) return;

    setCreateAccount(true);

    try {
      const res = await fetch(`${Config.API.API_BASE_URL}${Config.ENDPOINTS.USER_COMPLETE_REGISTRATION}`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          token,
          firstName: formData.firstName,
          lastName: formData.lastName,
          timeZone: formData.timeZone,
          password: formData.password,
          mfa: formData.useMFA,
          tou: formData.acceptTOU,
        }),
      });

      if (res.ok) {
        setAccountCreated(true);
        fireConfetti();
      } else {
        messageRef.current.innerHTML = "❌ Registration failed.";
        setMessageClass("warning");
        messageRef.current.style.display = "block";
      }
    } catch (err) {
      messageRef.current.innerHTML = `❌ Error creating account: ${err}`;
      setMessageClass("warning");
      messageRef.current.style.display = "block";
    } finally {
      setCreateAccount(false);
    }
  };

  const scorePassword = (password) => {
    let score = 0;
    if (password.length >= 8) score++;
    if (/[A-Z]/.test(password)) score++;
    if (/[0-9]/.test(password)) score++;
    if (/[^A-Za-z0-9]/.test(password)) score++;
    return score;
  };

  useEffect(() => {
    const handleFocusFirstName = (e) => {
      // Don't steal focus if the user is interacting with other input or select elements
      if (e.target.closest("input, select, textarea, button")) return;

      // Only force focus if firstName is empty
      if (!formData.firstName.trim() && inputRef.current) {
        e.preventDefault();
        e.stopPropagation();
        inputRef.current.focus();
      }
    };

    document.addEventListener("mousedown", handleFocusFirstName, true);
    return () => document.removeEventListener("mousedown", handleFocusFirstName, true);
  }, [formData.firstName]);

  return (
    <div className="login-container-dashboard" id="create-account">
      <div className="login-box" id="register-container">
        {accountCreated ? (
          <div className="success-box">
            <div className="success-header">
              <span className="success-emoji">🎉</span>
              <h2>Congratulations!</h2>
            </div>
            <p className="success-message">
              Your account has been successfully created.
            </p>
            <button className="success-button" onClick={() => navigate("/login")}>
              Go to Login Now
            </button>
          </div>
        ) : (
          <>
            <h2>
              <FontAwesomeIcon icon="user-plus" /> Create Account
            </h2>
            <div ref={messageRef} className={messageClass} id="message" />
            <form onSubmit={handleRegister}>
              <input
                type="email"
                name="email"
                value={email}
                readOnly
                className="readonly-input"
              />
              <input
                type="text"
                name="firstName"
                placeholder="First Name"
                value={formData.firstName}
                onChange={handleChange}
                ref={inputRef}
                autoComplete="first-name"
              />
              <input
                type="text"
                name="lastName"
                placeholder="Last Name"
                value={formData.lastName}
                onChange={handleChange}
                autoComplete="last-name"
              />
              <select
                name="timeZone"
                value={formData.timeZone}
                onChange={handleChange}
              >
                <option value="">Select Time Zone</option>
                {timeZones.map((tz) => (
                  <option key={tz.value} value={tz.value}>
                    (UTC {tz.offset}) {tz.name}
                  </option>
                ))}
              </select>

              <div className="mfa-toggle mfa-toggle-register">
                <input
                  id="useMFA"
                  name="useMFA"
                  type="checkbox"
                  checked={formData.useMFA}
                  onChange={handleChange}
                />
                <label htmlFor="useMFA">Enable Email Multifactor Authentication</label>
              </div>

              <div className="password-container">
                <input
                  type={showPassword ? "text" : "password"}
                  placeholder="Password"
                  name="password"
                  value={formData.password}
                  onChange={handleChange}
                  autoComplete="new-password"
                />
                <button
                  type="button"
                  className="toggle-password-create"
                  onClick={() => setShowPassword(!showPassword)}
                  tabIndex={-1}
                >
                  {showPassword ? "🙈 Hide" : "👁️ Show"}
                </button>
              </div>
              {formData.password && (
                <div className="strength-bar-container">
                  <div className={`strength-bar fill-${passwordScore}`} />
                </div>
              )}
              {formData.password && (
                <p className="password-hint">
                  Use at least 8 characters with letters, numbers, and symbols.
                </p>
              )}
              <input
                type="password"
                name="confirmPassword"
                placeholder="Confirm Password"
                value={formData.confirmPassword}
                onChange={handleChange}
                autoComplete="new-password"
              />
              {formData.confirmPassword && (
                <p
                  className={`password-match ${passwordsMatch ? "match" : "no-match"
                    }`}
                >
                  {passwordsMatch ? "✅ Passwords match" : "❌ Passwords do not match"}
                </p>
              )}

              <div className="mfa-toggle tou-toggle-register">
                <input
                  id="acceptTOU"
                  name="acceptTOU"
                  type="checkbox"
                  checked={formData.acceptTOU}
                  onChange={handleChange}
                />
                <label htmlFor="acceptTOU">
                  I agree to the <a href="/terms" target="_blank" rel="noopener noreferrer">Terms of Use</a>
                  &nbsp;and <a href="/privacy" target="_blank" rel="noopener noreferrer">Privacy Policy</a>.
                </label>
              </div>

              <p className="cookie-notice">
                By registering, you acknowledge that this site uses cookies and  and similar technologies essential for its operation.
              </p>

              <button type="submit" className="login-button">Register</button>
            </form>
          </>
        )}
      </div>

      <Spinner show={createAccount} />

    </div>
  );
};

export default CreateAccount;